import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { AlertTriangle, Calendar, FileText, Receipt, Mail, Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function daysLabel(n) {
  if (n < 0) return `${Math.abs(n)} days overdue`;
  if (n === 0) return "Due today";
  if (n === 1) return "Due tomorrow";
  return `${n} days`;
}

function badge(n) {
  if (n < 0) return "bg-red-600 text-white";
  if (n <= 7) return "bg-orange-600 text-white";
  if (n <= 30) return "bg-yellow-500 text-black";
  return "bg-zinc-200 text-zinc-700";
}

export default function CompliancePage() {
  const [days, setDays] = useState("90");
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get(`/compliance/reminders?days=${days}`).then((r) => setData(r.data));
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const [sendingDigest, setSendingDigest] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Schedule state
  const [schedule, setSchedule] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const loadSchedule = useCallback(() => {
    api.get("/schedule/digest").then((r) => setSchedule(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const sendDigest = async () => {
    setSendingDigest(true);
    setConfirmOpen(false);
    try {
      const { data: res } = await api.post(`/compliance/send-digest?days=${days}`);
      toast.success("Digest sent", {
        description: `Emailed ${res.sent}/${res.total_recipients} recipient(s).`,
      });
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSendingDigest(false);
    }
  };

  const saveSchedule = async (patch) => {
    if (!schedule) return;
    setScheduleSaving(true);
    try {
      const next = { ...schedule, ...patch };
      const { data } = await api.put("/schedule/digest", {
        enabled: !!next.enabled,
        day_of_week: next.day_of_week || "mon",
        hour: Number(next.hour) || 9,
        minute: Number(next.minute) || 0,
        days_horizon: Number(next.days_horizon) || 7,
      });
      setSchedule(data);
      toast.success(patch.enabled === false ? "Auto-digest paused" : "Schedule updated");
    } catch (e) {
      toast.error("Failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setScheduleSaving(false);
    }
  };

  const docsAll = data ? [
    ...data.documents.overdue.map((d) => ({ ...d, _kind: "doc" })),
    ...data.documents.upcoming.map((d) => ({ ...d, _kind: "doc" })),
  ] : [];
  const invsAll = data ? [
    ...data.invoices.overdue.map((d) => ({ ...d, _kind: "inv" })),
    ...data.invoices.upcoming.map((d) => ({ ...d, _kind: "inv" })),
  ] : [];

  return (
    <div data-testid="compliance-page">
      <PageHeader
        overline="RISK & RENEWALS"
        title="Compliance Calendar"
        description="Track document expiries (permits, royalty, agreements, quarry) and invoice due dates. Never miss a renewal."
        actions={
          <div className="flex items-center gap-2">
            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="compliance-send-digest-btn"
                  variant="outline"
                  disabled={sendingDigest}
                  className="rounded-sm border-zinc-300 hover:border-orange-600 hover:text-orange-600"
                >
                  {sendingDigest ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  Email Digest
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-sm" data-testid="compliance-confirm-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-heading">Send compliance digest?</AlertDialogTitle>
                  <AlertDialogDescription>
                    An email digest for the next <b>{days} days</b> will be sent to <b>all Admin and Manager users</b>.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="compliance-confirm-cancel" className="rounded-sm">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="compliance-confirm-send"
                    onClick={sendDigest}
                    className="bg-orange-600 hover:bg-orange-700 rounded-sm"
                  >
                    Send now
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Select value={days} onValueChange={setDays}>
              <SelectTrigger data-testid="compliance-horizon" className="w-40 rounded-sm border-zinc-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Next 30 days</SelectItem>
                <SelectItem value="60">Next 60 days</SelectItem>
                <SelectItem value="90">Next 90 days</SelectItem>
                <SelectItem value="180">Next 180 days</SelectItem>
                <SelectItem value="365">Next 365 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      {data && (data.documents.overdue.length + data.invoices.overdue.length) > 0 && (
        <div className="mb-6 border border-red-200 bg-red-50 rounded-sm p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
          <div>
            <div className="font-heading font-bold text-red-900">Action required</div>
            <div className="text-sm text-red-800">
              {data.documents.overdue.length + data.invoices.overdue.length} item(s) already overdue.
            </div>
          </div>
        </div>
      )}

      {/* Weekly digest scheduler */}
      {schedule && (
        <div data-testid="digest-schedule-card" className="mb-6 bg-white border border-zinc-200 rounded-sm">
          <div className="flex items-center gap-3 px-5 py-3 border-b border-zinc-200 bg-zinc-50">
            <Clock className="h-4 w-4 text-orange-600" />
            <div>
              <div className="font-heading font-bold text-sm">Auto-Digest Schedule</div>
              <div className="text-xs text-zinc-500">Send this report to all admins & managers on a recurring basis.</div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Label htmlFor="digest-enabled" className="text-xs text-zinc-600">
                {schedule.enabled ? "Enabled" : "Disabled"}
              </Label>
              <Switch
                id="digest-enabled"
                data-testid="digest-enabled-switch"
                checked={!!schedule.enabled}
                disabled={scheduleSaving}
                onCheckedChange={(v) => saveSchedule({ enabled: v })}
              />
            </div>
          </div>
          <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="overline">Day of week</Label>
              <Select
                value={schedule.day_of_week}
                onValueChange={(v) => saveSchedule({ day_of_week: v })}
                disabled={!schedule.enabled || scheduleSaving}
              >
                <SelectTrigger data-testid="digest-day-select" className="rounded-sm mt-1 border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[
                    ["mon","Monday"],["tue","Tuesday"],["wed","Wednesday"],["thu","Thursday"],
                    ["fri","Friday"],["sat","Saturday"],["sun","Sunday"],
                  ].map(([v,l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Hour (UTC)</Label>
              <Select
                value={String(schedule.hour)}
                onValueChange={(v) => saveSchedule({ hour: v })}
                disabled={!schedule.enabled || scheduleSaving}
              >
                <SelectTrigger data-testid="digest-hour-select" className="rounded-sm mt-1 border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({length:24}).map((_,h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2,"0")}:00</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Horizon (days)</Label>
              <Select
                value={String(schedule.days_horizon)}
                onValueChange={(v) => saveSchedule({ days_horizon: v })}
                disabled={!schedule.enabled || scheduleSaving}
              >
                <SelectTrigger data-testid="digest-horizon-select" className="rounded-sm mt-1 border-zinc-300"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[7,14,30,60,90].map((n) => <SelectItem key={n} value={String(n)}>{n} days</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="text-xs font-mono text-zinc-600">
              {schedule.next_run ? (
                <>
                  <div className="overline">Next run</div>
                  <div className="mt-1 text-zinc-900">{new Date(schedule.next_run).toLocaleString()}</div>
                </>
              ) : (
                <span className="text-zinc-400">Enable to schedule</span>
              )}
              {schedule.last_run && (
                <div className="mt-1 text-[10px] text-zinc-400">
                  Last run: {new Date(schedule.last_run).toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Documents */}
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
            <FileText className="h-4 w-4 text-orange-600" />
            <div className="font-heading font-bold">Document Expiries</div>
            <span className="ml-auto overline">{docsAll.length}</span>
          </div>
          {docsAll.length === 0 ? (
            <div className="p-10 text-center text-zinc-400 font-mono text-xs">
              No documents expiring in this window.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {docsAll.map((d) => (
                <li key={d.id} data-testid={`compliance-doc-${d.id}`} className="px-5 py-3 flex items-center gap-3 hover:bg-zinc-50">
                  <Calendar className="h-4 w-4 text-zinc-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{d.original_name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="font-mono text-[10px] px-1.5 py-0.5 border border-zinc-300 rounded-sm">
                        {d.category}
                      </span>
                      <span className="text-xs text-zinc-500 font-mono">expires {d.expiry_date}</span>
                    </div>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-1 rounded-sm ${badge(d.days_left)}`}>
                    {daysLabel(d.days_left)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Invoices */}
        <div className="bg-white border border-zinc-200 rounded-sm">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
            <Receipt className="h-4 w-4 text-orange-600" />
            <div className="font-heading font-bold">Invoice Due Dates</div>
            <span className="ml-auto overline">{invsAll.length}</span>
          </div>
          {invsAll.length === 0 ? (
            <div className="p-10 text-center text-zinc-400 font-mono text-xs">
              No pending invoices in this window.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {invsAll.map((i) => (
                <li key={i.id} data-testid={`compliance-inv-${i.id}`} className="px-5 py-3 flex items-center gap-3 hover:bg-zinc-50">
                  <Receipt className="h-4 w-4 text-zinc-400" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {i.invoice_number} — ₹{Number(i.amount).toLocaleString("en-IN")}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="font-mono text-[10px] px-1.5 py-0.5 border border-zinc-300 rounded-sm">
                        {i.invoice_type}
                      </span>
                      <span className="text-xs text-zinc-500 font-mono">due {i.due_date}</span>
                    </div>
                  </div>
                  <span className={`text-[10px] font-mono px-2 py-1 rounded-sm ${badge(i.days_left)}`}>
                    {daysLabel(i.days_left)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-8 text-xs text-zinc-500 font-mono">
        Tip: When uploading agreements, permits, quarry leases or royalty challans, set an <b>Expiry Date</b> in the upload form to receive automatic reminders here.
      </div>
    </div>
  );
}
